import { createSlice } from '@reduxjs/toolkit';
import {
  AudioPropertiesInfo,
  LocalAudioStats,
  NetworkQuality,
  RemoteAudioStats,
} from '@volcengine/rtc';
// import RtcClient from '@/lib/RtcClient';
import { analyzeContent } from '@/utils/contentDetector';

import logger from '@/utils/logger';

export interface IUser {
  username?: string;
  userId?: string;
  publishAudio?: boolean;
  publishVideo?: boolean;
  publishScreen?: boolean;
  audioStats?: RemoteAudioStats;
  audioPropertiesInfo?: AudioPropertiesInfo;
}

export type LocalUser = Omit<IUser, 'audioStats'> & {
  loginToken?: string;
  audioStats?: LocalAudioStats;
};

export interface Msg {
  value: string;
  time: string;
  user: string;
  paragraph?: boolean;
  definite?: boolean;
  isInterrupted?: boolean;
  feedback?: 'like' | 'dislike' | null;
  suggestions?: string[];
  sequence?: number;
}

export interface SceneConfig {
  id: string;
  icon?: string;
  name?: string;
  questions?: string[];
  botName: string;
  isVision: boolean;
  isScreenMode: boolean;
  isInterruptMode: boolean;
  keywordPopups?: KeywordPopupConfig[];
}

// 关键词弹窗配置
export interface KeywordPopupConfig {
  keywords: string[];
  /** @deprecated 使用 images 数组代替 */
  image?: string;
  /** 图片序列，支持多张图片轮播 */
  images?: string[];
  title?: string;
  description?: string;
  link?: string;
}

export interface RTCConfig {
  AppId: string;
  RoomId: string;
  UserId: string;
  Token: string;
}

export interface RoomState {
  time: number;
  roomId?: string;
  localUser: LocalUser;
  remoteUsers: IUser[];
  autoPlayFailUser: string[];
  /**
   * @brief 是否已加房
   */
  isJoined: boolean;
  /**
   * @brief 选择的场景
   */
  scene: string;
  /**
   * @brief 场景下的配置
   */
  sceneConfigMap: Record<string, SceneConfig>;
  /**
   * @brief RTC 相关的配置
   */
  rtcConfigMap: Record<string, RTCConfig>;

  /**
   * @brief AI 通话是否启用
   */
  isAIGCEnable: boolean;
  /**
   * @brief AI 是否正在说话
   */
  isAITalking: boolean;
  /**
   * @brief AI 思考中
   */
  isAIThinking: boolean;
  /**
   * @brief 用户是否正在说话
   */
  isUserTalking: boolean;
  /**
   * @brief 网络质量
   */
  networkQuality: NetworkQuality;

  /**
   * @brief 对话记录
   */
  msgHistory: Msg[];

  /**
   * @brief 是否待机
   */
  isStandby: boolean;

  /**
   * @brief AI消息累积文本（用于智能分句）
   */
  currentAICumulative: string;

  /**
   * @brief 超时退出设置（毫秒）
   * 0 = 不超时退出
   * 15000 = 15秒
   * 30000 = 30秒
   * 60000 = 1分钟
   */
  autoExitTimeout: number;

  /**
   * @brief 当前激活的关键词弹窗列表（支持多个同时显示）
   */
  activeKeywordPopups: KeywordPopupConfig[];

  /**
   * @brief 已触发过的关键词（防止重复触发）
   */
  triggeredKeywords: string[];
}

const initialState: RoomState = {
  time: -1,
  scene: '',
  sceneConfigMap: {},
  rtcConfigMap: {},
  remoteUsers: [],
  localUser: {
    publishAudio: false,
    publishVideo: false,
    publishScreen: false,
  },
  autoPlayFailUser: [],
  isJoined: false,
  isAIGCEnable: false,
  isAIThinking: false,
  isAITalking: false,
  isUserTalking: false,
  networkQuality: NetworkQuality.UNKNOWN,
  msgHistory: [],
  isStandby: false,
  currentAICumulative: '',
  autoExitTimeout: 30000,
  activeKeywordPopups: [],
  triggeredKeywords: [],
};

export const roomSlice = createSlice({
  name: 'room',
  initialState,
  reducers: {
    localJoinRoom: (
      state,
      {
        payload,
      }: {
        payload: {
          roomId: string;
          user: LocalUser;
        };
      }
    ) => {
      state.roomId = payload.roomId;
      state.localUser = {
        ...state.localUser,
        ...payload.user,
      };
      state.isJoined = true;
    },
    localLeaveRoom: (state) => {
      state.roomId = undefined;
      state.time = -1;
      state.localUser = {
        publishAudio: false,
        publishVideo: false,
        publishScreen: false,
      };
      state.remoteUsers = [];
      state.isJoined = false;
    },
    remoteUserJoin: (state, { payload }) => {
      state.remoteUsers.push(payload);
    },
    remoteUserLeave: (state, { payload }) => {
      const findIndex = state.remoteUsers.findIndex((user) => user.userId === payload.userId);
      state.remoteUsers.splice(findIndex, 1);
    },

    updateScene: (state, { payload }) => {
      state.scene = payload;
      // 仅记录场景切换，不在此处更新 basicInfo，避免与 updateRTCConfig/Join 重叠
      if (process.env.NODE_ENV === 'development') {
        logger.info(`[RTC] 场景切换到 ${payload}`);
      }
    },

    updateSceneConfig: (state, { payload }) => {
      state.sceneConfigMap = payload;
    },

    updateRTCConfig: (state, { payload }) => {
      // 仅存储配置，副作用（basicInfo）归口到 Join 流程
      state.rtcConfigMap = payload;
      if (Object.keys(payload).length === 0) {
        logger.warn('[Warn] 无法设置 RTC 配置: 没有可用的场景配置');
      }
    },

    updateLocalUser: (state, { payload }: { payload: Partial<LocalUser> }) => {
      state.localUser = {
        ...state.localUser,
        ...(payload || {}),
      };
    },

    updateNetworkQuality: (state, { payload }) => {
      state.networkQuality = payload.networkQuality;
    },

    updateRemoteUser: (state, { payload }: { payload: IUser | IUser[] }) => {
      if (!Array.isArray(payload)) {
        payload = [payload];
      }

      payload.forEach((user) => {
        const findIndex = state.remoteUsers.findIndex((u) => u.userId === user.userId);
        state.remoteUsers[findIndex] = {
          ...state.remoteUsers[findIndex],
          ...user,
        };
      });
    },

    updateRoomTime: (state, { payload }) => {
      state.time = payload.time;
    },

    addAutoPlayFail: (state, { payload }) => {
      const autoPlayFailUser = state.autoPlayFailUser;
      const index = autoPlayFailUser.findIndex((item) => item === payload.userId);
      if (index === -1) {
        state.autoPlayFailUser.push(payload.userId);
      }
    },
    removeAutoPlayFail: (state, { payload }) => {
      const autoPlayFailUser = state.autoPlayFailUser;
      const _autoPlayFailUser = autoPlayFailUser.filter((item) => item !== payload.userId);
      state.autoPlayFailUser = _autoPlayFailUser;
    },
    clearAutoPlayFail: (state) => {
      state.autoPlayFailUser = [];
    },
    updateAIGCState: (state, { payload }) => {
      state.isAIGCEnable = payload.isAIGCEnable;
    },
    updateAITalkState: (state, { payload }) => {
      state.isAIThinking = false;
      state.isUserTalking = false;
      state.isAITalking = payload.isAITalking;
    },
    updateAIThinkState: (state, { payload }) => {
      state.isAIThinking = payload.isAIThinking;
      state.isUserTalking = false;
    },
    updateUserTalking: (state, { payload }) => {
      state.isUserTalking = payload.isUserTalking;
    },
    clearHistoryMsg: (state) => {
      state.msgHistory = [];
    },
    setHistoryMsg: (state, { payload }) => {
      const { text, user, paragraph, definite, sequence, suggestions = [] } = payload;
      const lastMsg = state.msgHistory.at(-1);
      const botName = state.sceneConfigMap[state.scene]?.botName;

      // 应用敏感词过滤（如果还没有过滤过）
      const result = analyzeContent(text, {
        enableSensitiveFilter: true,
        context: {
          isUserMessage: user !== botName,
          isAIMessage: user === botName,
          // 将定稿标记传递给内容检测，配合 logOnlyFinal 策略输出日志
          definite,
          isFinal: Boolean(definite),
        },
      });
      const filteredText = result.filteredText;

      if (user === botName) {
        // AI 消息
        if (
          !lastMsg ||
          lastMsg.user !== botName ||
          lastMsg.paragraph // 上一条AI消息已结束
        ) {
          state.msgHistory.push({
            value: filteredText,
            time: new Date().toString(),
            user,
            paragraph,
            definite,
            suggestions,
            sequence,
          });
          state.currentAICumulative = '';
        } else {
          // 智能分句累加
          if (definite) {
            state.currentAICumulative =
              (state.currentAICumulative || '') +
              (state.currentAICumulative ? ' ' : '') +
              filteredText;
          }
          lastMsg.value = state.currentAICumulative + (definite ? '' : filteredText);
          lastMsg.time = new Date().toString();
          lastMsg.paragraph = paragraph;
          lastMsg.definite = definite;
          if (suggestions.length) {
            lastMsg.suggestions = suggestions;
          }
          lastMsg.sequence = sequence;

          // 实时检测关键词弹窗（提到就弹出，不等整段结束）
          const sceneConfig = state.sceneConfigMap[state.scene];
          const keywordPopups = sceneConfig?.keywordPopups;
          if (keywordPopups && keywordPopups.length > 0) {
            const msgText = lastMsg.value.toLowerCase();
            // 遍历所有弹窗配置，找出所有匹配的
            for (const popup of keywordPopups) {
              for (const keyword of popup.keywords) {
                const normalizedKeyword = keyword.toLowerCase();
                if (
                  msgText.includes(normalizedKeyword) &&
                  !state.triggeredKeywords.includes(normalizedKeyword)
                ) {
                  // 找到匹配的关键词，添加到弹窗列表
                  if (!state.activeKeywordPopups.some((p) => p.title === popup.title)) {
                    state.activeKeywordPopups.push(popup);
                    logger.info('[Room] 实时检测到关键词弹窗:', popup.title, '关键词:', keyword);
                  }
                  state.triggeredKeywords.push(normalizedKeyword);
                  break; // 一个popup只需匹配一个关键词
                }
              }
            }
          }
        }
      } else if (!lastMsg || lastMsg.user !== user || lastMsg.paragraph) {
        state.msgHistory.push({
          value: filteredText,
          time: new Date().toString(),
          user,
          paragraph,
          definite,
          suggestions,
          sequence,
        });
      } else {
        lastMsg.value = filteredText;
        lastMsg.time = new Date().toString();
        lastMsg.paragraph = paragraph;
        lastMsg.definite = definite;
        if (suggestions.length) {
          lastMsg.suggestions = suggestions;
        }
        lastMsg.sequence = sequence;
      }
    },
    setInterruptMsg: (state) => {
      state.isAITalking = false;
      if (!state.msgHistory.length) {
        return;
      }
      /** 找到最后一个末尾的字幕, 将其状态置换为打断 */
      for (let id = state.msgHistory.length - 1; id >= 0; id--) {
        const msg = state.msgHistory[id];
        if (msg.value) {
          if (!msg.definite) {
            state.msgHistory[id].isInterrupted = true;
            state.msgHistory[id].definite = true;
          }
          break;
        }
      }
    },

    setStandby: (state, action) => {
      state.isStandby = action.payload;
    },
    resetChat: (state) => {
      state.msgHistory = [];
      state.isAITalking = false;
      state.isUserTalking = false;
    },
    setFeedback: (state, action) => {
      const { msgIndex, feedback } = action.payload;
      if (msgIndex >= 0 && state.msgHistory[msgIndex]) {
        // 设置特定消息的反馈
        state.msgHistory[msgIndex].feedback = feedback;
      } else if (msgIndex === -1) {
        // msgIndex 为 -1 时，设置最近一条 AI 消息的反馈
        const botName = state.sceneConfigMap[state.scene]?.botName;
        if (botName) {
          // 从后往前查找第一条属于 AI 的消息
          for (let i = state.msgHistory.length - 1; i >= 0; i--) {
            if (state.msgHistory[i].user === botName) {
              state.msgHistory[i].feedback = feedback;
              break;
            }
          }
        }
      }
    },
    setAutoExitTimeout: (state, { payload }) => {
      state.autoExitTimeout = payload;
    },
    // 关闭指定的关键词弹窗
    closeKeywordPopup: (state, { payload }: { payload?: string }) => {
      if (payload) {
        // 关闭指定title的弹窗
        state.activeKeywordPopups = state.activeKeywordPopups.filter((p) => p.title !== payload);
      } else {
        // 关闭所有弹窗
        state.activeKeywordPopups = [];
      }
    },
    // 手动设置关键词弹窗（用于测试）
    setKeywordPopup: (state, { payload }: { payload: KeywordPopupConfig | KeywordPopupConfig[] | null }) => {
      if (payload === null) {
        state.activeKeywordPopups = [];
      } else if (Array.isArray(payload)) {
        state.activeKeywordPopups = payload;
      } else if (!state.activeKeywordPopups.some((p) => p.title === payload.title)) {
        // 添加到列表（如果不存在）
        state.activeKeywordPopups.push(payload);
      }
    },
    // 重置关键词触发记录（切换场景时调用）
    resetTriggeredKeywords: (state) => {
      state.triggeredKeywords = [];
      state.activeKeywordPopups = [];
    },
    // 一键重置所有room状态
    resetRoomState: () => initialState,
  },
});

export const {
  localJoinRoom,
  localLeaveRoom,
  remoteUserJoin,
  remoteUserLeave,
  updateRemoteUser,
  updateLocalUser,
  updateRoomTime,
  addAutoPlayFail,
  removeAutoPlayFail,
  clearAutoPlayFail,
  updateAIGCState,
  updateAITalkState,
  updateAIThinkState,
  updateUserTalking,
  setHistoryMsg,
  clearHistoryMsg,
  setInterruptMsg,
  updateNetworkQuality,
  updateScene,
  updateSceneConfig,
  updateRTCConfig,
  setStandby,
  resetChat,
  setFeedback,
  setAutoExitTimeout,
  closeKeywordPopup,
  setKeywordPopup,
  resetTriggeredKeywords,
  resetRoomState,
} = roomSlice.actions;

export default roomSlice.reducer;
